import { CanceledError, isAxiosError } from 'axios'
import { ErroDoUsuarioPontotel } from '@/tipos/erros/classes'
import { CODIGOS_TECNICOS_ERRO, criarObservabilidadeDesativada } from '@next/compartilhados/observabilidade'
import type { ContextoErro, Observabilidade } from '@next/compartilhados/observabilidade'
import { obterDiagnosticoRespostaHttp } from '@next/infraestrutura/http/normalizar-falha-http'
import { naturezaFalhaHttp, obterContextoRequisicaoHttp } from '@next/infraestrutura/observabilidade/contexto-http'
import { converterErroHttpLegado } from './erro-http-legado'

const objeto = (valor: unknown): Record<string, unknown> => (
  valor !== null && typeof valor === 'object' ? valor as Record<string, unknown> : {}
)

export const normalizarErroLegado = (erro: unknown, extra?: unknown): ContextoErro | undefined => {
  try {
    if (erro instanceof CanceledError || (erro instanceof Error && erro.name === 'AbortError')) {
      return { origem: 'legado', categoria: 'cancelamento' }
    }
    if (erro instanceof ErroDoUsuarioPontotel) return { origem: 'legado', categoria: 'validacao' }
    const detalhes = objeto(objeto(erro).detalhesErroSentry ?? objeto(extra).detalhesErroSentry
      ?? objeto(objeto(extra).__serialized__).detalhesErroSentry)
    const axios = isAxiosError(erro) ? erro : undefined
    const resposta = objeto(erro)
    const temCorpoHttp = 'data' in resposta || 'body' in resposta
    if (axios?.code === 'ERR_CANCELED' || detalhes.codigoErroApi === 'ERR_CANCELED') return { origem: 'http', categoria: 'cancelamento' }
    const status = [axios?.response?.status, axios?.status, temCorpoHttp ? resposta.status : undefined, detalhes.codigoErroApi, detalhes.statusErroApi, detalhes.status]
      .find((valor): valor is number => typeof valor === 'number' && Number.isInteger(valor) && valor >= 100 && valor <= 599)
    const codigo = CODIGOS_TECNICOS_ERRO.find(valor => valor === (axios?.code ?? detalhes.codigoErroApi))
    const metodo = resposta.metodo ?? detalhes.metodo
    const url = resposta.url ?? detalhes.urlCompleta
    const diagnostico = axios ? obterDiagnosticoRespostaHttp(axios) : undefined
    if (axios || (temCorpoHttp && status !== undefined) || Object.keys(detalhes).length) return {
      origem: 'http', operacao: 'requisicao', categoria: 'inesperado',
      naturezaFalha: naturezaFalhaHttp(status, codigo),
      ...(status !== undefined ? { statusHttp: status } : {}),
      ...(codigo ? { codigoTecnico: codigo } : {}),
      ...(diagnostico?.mensagemRecebida ? { mensagemApi: diagnostico.mensagemRecebida } : {}),
      ...(diagnostico?.codigoRecebido ? { codigoApi: diagnostico.codigoRecebido } : {}),
      ...(diagnostico?.requestId ? { requestId: diagnostico.requestId } : {}),
      ...obterContextoRequisicaoHttp(axios?.config ?? {
        ...(typeof metodo === 'string' ? { method: metodo } : {}),
        ...(typeof url === 'string' ? { url } : {}),
      }),
    }
  } catch { /* Um formato hostil não deve impedir o registro mínimo da falha. */ }
  return undefined
}

let instancia: Observabilidade = criarObservabilidadeDesativada()

export const configurarObservabilidadeLegada = (observabilidade: Observabilidade): void => {
  instancia = observabilidade
}

/** Não conhece o SDK e não importa main/store. O bootstrap fornece a instância única. */
export const observabilidadeLegada: Observabilidade = {
  registrarErro(erro, contexto) {
    try {
      const normalizado = normalizarErroLegado(erro)
      const contextoCompleto: ContextoErro = {
        ...normalizado,
        ...contexto,
        ...(normalizado?.categoria === 'cancelamento' || normalizado?.categoria === 'validacao' ? { categoria: normalizado.categoria } : {}),
      }
      instancia.registrarErro(converterErroHttpLegado(erro, contextoCompleto), contextoCompleto)
    } catch { /* Diagnóstico não altera o fluxo de negócio. */ }
  },
}
